import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'

import StepForm from '../../../../features/components/Forms/StepForm'
import CampaignSelectionStep from './Steps/CampaignSelectionStep'
import CampaignNameStep from './Steps/CampaignNameStep'

import useSuccessCampaignFormSubmit from './useSuccessCampaignFormSubmit'
import { usePurifiedFormik } from '../../../../hooks/formHooks/usePurifiedFormik'

import { clearCampaigns, clearCreateCampaign } from '../../../../modules/actions/campaigns'
import { campaignCreateErrorSelector, campaignCreateIsLoadingSelector } from '../../../../modules/selectors/campaigns'

import { UPLOAD_CREATIVE_CAMPAIGN_CREATE } from '../../../../constants/forms'
import { LIST_ITEM_OPTION, OPTION_NEW_LIST_ITEM } from '../../../../hooks/formHooks/listItemFormHooks/fields'

const UploadAdCreativeCampaignForm = ({
  platform,
  formik,
  onSuccessSubmit,
  children,
  manualSuccessSubmit,
  adAccountId,
  isLoadingSelector,
  existedCampaigns,
  formProgressItems,
  campaignStepValidationFields,
  allowShowNameStep = true,
  advancedSettingsChildren,
  shouldClearCampaigns = true,
  campaignSelectionStepTopChildren,
  shouldFetchLineItems = true
}) => {
  const dispatch = useDispatch()

  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const { [LIST_ITEM_OPTION]: campaignOption } = values

  const clearHandler = useCallback(() => {
    if (shouldClearCampaigns) {
      dispatch(clearCampaigns())
    }
    dispatch(clearCreateCampaign())
  }, [dispatch, shouldClearCampaigns])

  const successSubmit = useSuccessCampaignFormSubmit({ campaignOption, platform, adAccountId, shouldFetchLineItems })

  const showCampaignNameStep = useMemo(() => {
    return values[LIST_ITEM_OPTION] === OPTION_NEW_LIST_ITEM && allowShowNameStep
  }, [values, allowShowNameStep])

  const newCampaignSteps = useMemo(() => {
    return [
      {
        component: (
          <CampaignSelectionStep
            existedCampaigns={existedCampaigns}
            additionalStepFields={campaignStepValidationFields}
            showContinueButton={showCampaignNameStep}
            advancedSettingsChildren={advancedSettingsChildren}
            topChildren={campaignSelectionStepTopChildren}
          >
            {children}
          </CampaignSelectionStep>
        ),
        show: true
      },
      {
        component: <CampaignNameStep formProgressItems={formProgressItems} />,
        show: showCampaignNameStep
      }
    ]
  }, [
    formProgressItems,
    children,
    existedCampaigns,
    campaignStepValidationFields,
    showCampaignNameStep,
    advancedSettingsChildren,
    campaignSelectionStepTopChildren
  ])

  return (
    <StepForm
      steps={newCampaignSteps}
      formik={formik}
      formName={UPLOAD_CREATIVE_CAMPAIGN_CREATE}
      submitText="Save and continue"
      successSubmit={manualSuccessSubmit || successSubmit}
      onSuccessSubmit={onSuccessSubmit}
      isLoadingSelector={isLoadingSelector || campaignCreateIsLoadingSelector}
      errorSelector={campaignCreateErrorSelector}
      clearSubmitHandler={clearHandler}
      formProgressItems={formProgressItems}
    />
  )
}

UploadAdCreativeCampaignForm.propTypes = {
  platform: PropTypes.string.isRequired,
  formik: PropTypes.object.isRequired,
  onSuccessSubmit: PropTypes.func.isRequired,
  adAccountId: PropTypes.string.isRequired,
  manualSuccessSubmit: PropTypes.bool,
  isLoadingSelector: PropTypes.func,
  existedCampaigns: PropTypes.array
}

export default UploadAdCreativeCampaignForm
